import type { StorageLike } from "../auth/session.js";
import { memoryStorage } from "../auth/session.js";

/**
 * Record ids this browser has seen.
 *
 * This exists because of a gap in the API, and it should go away when the gap
 * closes. There is no `GET /api/jobs` and no `GET /api/jobs/:id/surveys`: a job
 * or a survey can only be fetched by id. So the portal remembers the ids it has
 * been given — from creating a job, from opening one, from a deadline or a task
 * that refers to one — and looks those up.
 *
 * Two things this deliberately is not. It is not a cache: only ids are kept,
 * and every record is re-fetched from the API, so nothing stale is ever shown.
 * And it is not a security boundary: an id here is worthless without a session
 * that may read it, because the API answers a job outside your organisation
 * with a 404 whatever you know about it. Ids are namespaced by organisation so
 * that switching tenant does not litter the screen with 404s.
 */

const PREFIX = "sts.portal.known";

/** Enough to keep a working set; not a substitute for the missing index. */
const LIMIT = 200;

export type RecordKind = "job" | "survey";

export class KnownRecords {
  private readonly storage: StorageLike;

  constructor(storage: StorageLike = memoryStorage()) {
    this.storage = storage;
  }

  /** Most recently seen first. */
  list(kind: RecordKind, organisationId: string | null): readonly string[] {
    return this.read(kind, organisationId);
  }

  /** Surveys remembered against one job, for the job detail screen. */
  listForJob(
    kind: RecordKind,
    organisationId: string | null,
    jobId: string,
  ): readonly string[] {
    return this.read(`${kind}@${jobId}`, organisationId);
  }

  remember(
    kind: RecordKind,
    organisationId: string | null,
    id: string,
    opts: { jobId?: string } = {},
  ): void {
    this.write(kind, organisationId, id);
    if (opts.jobId !== undefined) {
      this.write(`${kind}@${opts.jobId}`, organisationId, id);
    }
  }

  forget(kind: RecordKind, organisationId: string | null, id: string): void {
    const scope = this.key(kind, organisationId);
    const next = this.read(kind, organisationId).filter((x) => x !== id);
    this.storage.setItem(scope, JSON.stringify(next));
  }

  private read(scope: string, organisationId: string | null): string[] {
    const raw = this.storage.getItem(this.key(scope, organisationId));
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (!Array.isArray(parsed)) return [];
      return parsed.filter((id): id is string => typeof id === "string");
    } catch {
      // Corrupt storage costs a rebuilt list, not a broken screen.
      return [];
    }
  }

  private write(
    scope: string,
    organisationId: string | null,
    id: string,
  ): void {
    const existing = this.read(scope, organisationId).filter((x) => x !== id);
    this.storage.setItem(
      this.key(scope, organisationId),
      JSON.stringify([id, ...existing].slice(0, LIMIT)),
    );
  }

  private key(scope: string, organisationId: string | null): string {
    return `${PREFIX}.${organisationId ?? "default"}.${scope}`;
  }
}
